/**
 * TEMP-H-004: Stage Settings & Persona Thresholds Types
 * 
 * TypeScript interfaces for stage settings API, validation, and UI state.
 * Defines contracts for persona-based QC thresholds and stage configuration.
 */

import type { StageName } from '@/lib/shared-types';

// Base Settings Types
export interface PersonaThreshold {
  personaSlug: string;
  personaName: string;
  personaRole: string;
  passMin: number;        // 0-1 range
  fallbackMin: number;    // 0-1 range
  categoryThresholds: Record<string, number>; // 0-1 range per category
  categoryWeights?: Record<string, number>;   // Optional weights for weighted score
  retryLimit: number;
  version: string;        // Schema version for migrations
  hash: string;           // Hash for change detection
}

export interface StageSettingsConfig {
  executionId: string;
  stage: StageName;
  isEditable: boolean;    // Based on user role and execution state
  overriddenFromLaunch: boolean; // True if different from launch config
  
  // Current active persona
  activePersona: PersonaThreshold;
  
  // All available personas for this stage
  availablePersonas: PersonaThreshold[];
  
  // Metadata
  version: string;
  updatedAt: string;
  updatedBy?: string;
  
  // Launch defaults for comparison
  launchDefaults?: PersonaThreshold;
}

// API Request/Response Types
export interface UpdateStageSettingsRequest {
  personaSlug?: string;   // Switch to different persona
  passMin?: number;
  fallbackMin?: number;
  categoryThresholds?: Record<string, number>;
  categoryWeights?: Record<string, number>;
  retryLimit?: number;
  reason?: string;        // Optional reason for audit trail
}

export interface UpdateStageSettingsResponse {
  success: boolean;
  message: string;
  settings: StageSettingsConfig;
  auditEventId?: string;
  timestamp: string;
}

export interface StageSettingsError {
  type: 'validation' | 'permission' | 'conflict' | 'network';
  field?: string;
  message: string;
  details?: Record<string, any>;
}

// Validation Schema
export interface ThresholdValidationRules {
  passMin: { min: number; max: number };
  fallbackMin: { min: number; max: number };
  categoryThresholds: { min: number; max: number };
  categoryWeights: { min: number; max: number; sumMustEqual?: number };
  retryLimit: { min: number; max: number };
  
  // Cross-field validation
  passMinMustBeGreaterThanFallback: boolean;
  maxCategoryCount?: number;
}

export const DEFAULT_VALIDATION_RULES: ThresholdValidationRules = {
  passMin: { min: 0.1, max: 1.0 },
  fallbackMin: { min: 0.05, max: 0.95 },
  categoryThresholds: { min: 0.0, max: 1.0 },
  categoryWeights: { min: 0.0, max: 1.0, sumMustEqual: 1.0 },
  retryLimit: { min: 1, max: 10 },
  passMinMustBeGreaterThanFallback: true,
  maxCategoryCount: 10
};

// UI State Types
export interface SettingsFormState {
  isDirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  errors: Record<string, string>;
  originalValues: Partial<PersonaThreshold>;
  currentValues: Partial<PersonaThreshold>;
}

export interface SettingsDialogState {
  isOpen: boolean;
  mode: 'view' | 'edit' | 'create';
  selectedPersonaSlug?: string;
  showAdvanced: boolean;
}

// Audit Event Types (for Events v2 integration)
export interface StageSettingsAuditEvent {
  eventType: 'stage_settings_updated';
  executionId: string;
  stage: StageName;
  userId: string;
  timestamp: string;
  
  // Compact diff for audit trail
  changes: {
    field: string;
    before: any;
    after: any;
  }[];
  
  reason?: string;
  personaChanged?: {
    from: string;
    to: string;
  };
}

// Display Configuration
export interface CategoryDisplayConfig {
  name: string;
  key: string;
  description: string;
  weight?: number;
  color?: string;
  icon?: string;
}

export const COMMON_CATEGORIES: Record<string, CategoryDisplayConfig> = {
  narrative_clarity: {
    name: 'Narrative Clarity',
    key: 'narrative_clarity',
    description: 'How clear and coherent the narrative elements are',
    color: 'blue',
    icon: '📖'
  },
  visual_coherence: {
    name: 'Visual Coherence', 
    key: 'visual_coherence',
    description: 'Consistency of visual style and composition',
    color: 'purple',
    icon: '🎨'
  },
  technical_quality: {
    name: 'Technical Quality',
    key: 'technical_quality', 
    description: 'Technical execution and production values',
    color: 'green',
    icon: '⚙️'
  },
  creative_impact: {
    name: 'Creative Impact',
    key: 'creative_impact',
    description: 'Creative originality and emotional impact',
    color: 'orange',
    icon: '✨'
  },
  persona_consistency: {
    name: 'Persona Consistency',
    key: 'persona_consistency',
    description: 'Alignment with chosen persona and style',
    color: 'teal',
    icon: '🎭'
  }
};

// React Query Keys
export const stageSettingsKeys = {
  all: ['stageSettings'] as const,
  execution: (executionId: string) => ['stageSettings', executionId] as const,
  stage: (executionId: string, stage: StageName) => 
    ['stageSettings', executionId, stage] as const,
  personas: (executionId: string, stage: StageName) => 
    ['stageSettings', executionId, stage, 'personas'] as const,
} as const;

// Utility Types
export type SettingsPermission = 'read' | 'edit' | 'admin';

export interface SettingsPermissionCheck {
  canView: boolean;
  canEdit: boolean;
  canEditAdvanced: boolean;
  canSwitchPersona: boolean;
  canViewAuditLog: boolean;
  effectiveRole: SettingsPermission;
}

// Form Validation Results
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
  warnings?: Record<string, string>;
}

// Category Threshold Editor State
export interface CategoryEditorState {
  categories: Record<string, {
    threshold: number;
    weight?: number;
    isValid: boolean;
    error?: string;
  }>;
  totalWeight: number;
  isWeightedSumValid: boolean;
}